
/**
 * The baseline's weights, scaled to leave room for this assignment's extra criteria.
 *
 * 🔴 A MIRROR OF `api/turbine/domain/rubric.py`, AND THE SERVER IS THE AUTHORITY. This exists
 * only so a teacher can see the baseline shrink as they type — adding a criterion is not
 * free, and a form that lists only what you typed hides the cost entirely. The server
 * recomputes it and refuses independently; if the two ever disagree, the server wins and the
 * teacher was shown a lie.
 *
 * 🔴 SO THE TWO ARE GATED AGAINST EACH OTHER. `src/rubric.vectors.json` is generated from the
 * Python and asserted here by vitest and there by pytest — the same fixture, checked from
 * both sides. Two implementations of one rule with nothing comparing them is the drift this
 * codebase has paid for repeatedly.
 *
 * ⚠️ LARGEST REMAINDER, NOT ROUNDING. `validate_rubric` refuses a rubric whose weights do not
 * sum to the maximum, and scaling integers by a fraction does not sum: truncation loses
 * points, rounding each independently loses or gains several. Floor everything, then hand the
 * shortfall to the largest fractional parts — ties broken by criterion id, so the same
 * configuration cannot produce two different answers.
 */
/* ⚠️ GENERIC OVER THE ROW, because the arithmetic reads `id` and `weight` and nothing else.
   Typed as `Criterion[]` it forced the generated vectors to carry every display field the
   real type has — so adding `label` and `credit_for` to `Criterion` broke a fixture about
   allocation, which has no opinion about either. Whatever comes in comes back out. */
export function scaleBaseline<T extends { id: string; weight: number }>(
  criteria: readonly T[], target: number): T[] {
  const total = criteria.reduce((n, c) => n + (c.weight || 0), 0);
  if (total <= 0 || target <= 0) {
    return criteria.map((c) => ({ ...c, weight: 0 }));
  }
  // ⚠️ `noUncheckedIndexedAccess` is on, so every index is `T | undefined`. Zipping into
  // one array of objects keeps the arithmetic total rather than sprinkling `!` over it.
  const rows = criteria.map((c) => {
    const exact = ((c.weight || 0) * target) / total;
    const floor = Math.floor(exact);
    return { id: String(c.id), exact, weight: floor, frac: exact - floor };
  });
  const shortfall = target - rows.reduce((a, r) => a + r.weight, 0);

  const order = [...rows].sort((a, b) => (b.frac - a.frac) || a.id.localeCompare(b.id));
  for (let k = 0; k < shortfall; k++) {
    const row = order[k];
    if (row) row.weight += 1;
  }
  return criteria.map((c, i) => ({ ...c, weight: rows[i]?.weight ?? 0 }));
}

/** What a criterion does on one assignment: counts, judged and reported without counting, or
 *  not judged at all. The binding's `criteria_overrides[id].mode`, absent meaning "score". */
export type CriterionMode = "score" | "advisory" | "off";

/** One criterion's worth on an assignment: its points if it counts, or the points it would
 *  have, shown and not counted, if it is advisory. An off criterion has neither. */
export interface CriterionPoints { id: string; mode: CriterionMode; points: number | null;
                                   would: number | null }

/** Why the server would refuse this configuration, in `rubric.effective`'s own order: every
 *  criterion off; nothing scored; none of the rubric's own criteria scored; extras worth the
 *  whole maximum; a rubric criterion scaled to nothing. Null when it would be accepted. */
export type Refusal = "all_off" | "none_scored" | "no_base_scored" | "over" | "zeroed";

/**
 * What every criterion is worth on an assignment, as grading will see it.
 *
 * 🔴 A MIRROR OF THE PARTITION IN `rubric.effective`, AND THE SERVER IS THE AUTHORITY. The
 * criteria that count share what the counted extras leave of the maximum, by `scaleBaseline`
 * (largest remainder, the allocation gated above); an advisory criterion shows the weight it
 * was given and counts nothing; an off one is dropped. Gated by the `points` vectors, which
 * are `effective()` itself across every mix of modes — the refusals included, so the editor
 * warns on exactly the configurations the server would refuse, and on no others.
 * ⚠️ A REFUSED CONFIGURATION STILL GETS ITS ROWS, so the editor can show what is wrong while
 * somebody is typing; only an `over` one has no points for the rubric's criteria, because
 * there is nothing left to share.
 */
export function effectivePoints(
  base: readonly { id: string; weight: number }[],
  extras: readonly { id: string; weight: number }[],
  modeOf: (id: string) => CriterionMode,
  max: number,
): { rows: CriterionPoints[]; extraTotal: number; refused: Refusal | null; zeroed: string[];
     counting: number } {
  const all = [...base, ...extras];
  const baseScored = base.filter((c) => modeOf(c.id) === "score");
  const extraScored = extras.filter((c) => modeOf(c.id) === "score");
  const extraTotal = extraScored.reduce((n, c) => n + (c.weight || 0), 0);
  const over = extraTotal >= max;
  const scaled = over ? [] : scaleBaseline(baseScored, max - extraTotal);
  const zeroed = scaled.filter((c) => c.weight === 0).map((c) => c.id);
  const counting = baseScored.length + extraScored.length;
  const refused: Refusal | null =
    all.every((c) => modeOf(c.id) === "off") ? "all_off"
    : counting === 0 ? "none_scored"
    : baseScored.length === 0 ? "no_base_scored"
    : over ? "over"
    : zeroed.length > 0 ? "zeroed"
    : null;
  const byId = new Map(scaled.map((c) => [c.id, c.weight]));
  const isBase = new Set(base.map((c) => c.id));
  const rows = all.map((c): CriterionPoints => {
    const mode = modeOf(c.id);
    if (mode === "off") return { id: c.id, mode, points: null, would: null };
    if (mode === "advisory") return { id: c.id, mode, points: null, would: c.weight || 0 };
    return { id: c.id, mode,
             points: !isBase.has(c.id) ? (c.weight || 0) : over ? null : (byId.get(c.id) ?? 0),
             would: null };
  });
  return { rows, extraTotal, refused, zeroed, counting };
}
